import React, { forwardRef } from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, icon, className = '', id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative group">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-neutral-400 group-focus-within:text-primary-500 transition-colors duration-200">
              {icon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={`
              block w-full rounded-xl border bg-white/70 dark:bg-[#1a1a2e]/80 backdrop-blur-sm px-4 py-3 text-sm
              dark:text-white
              placeholder:text-neutral-400 dark:placeholder:text-neutral-500
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-offset-0
              focus:bg-white dark:focus:bg-[#1a1a2e] focus:shadow-md
              disabled:bg-neutral-100 dark:disabled:bg-[#1a1a2e]/40 disabled:text-neutral-400 disabled:cursor-not-allowed
              ${icon ? 'pl-11' : ''}
              ${
                error
                  ? 'border-error-500/60 focus:ring-error-500/20 focus:border-error-500 animate-wiggle'
                  : 'border-neutral-200/80 dark:border-indigo-500/20 focus:ring-primary-500/20 focus:border-primary-400 dark:focus:border-indigo-500/40'
              }
              ${className}
            `.trim()}
            {...props}
          />
        </div>
        {error && (
          <p className="mt-1.5 text-sm text-error-500 flex items-center gap-1 animate-slide-down">
            <span>&#x26A0;</span> {error}
          </p>
        )}
        {hint && !error && (
          <p className="mt-1.5 text-sm text-neutral-400 dark:text-neutral-500">{hint}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
